/**
 * World Sync Service — automatic live sync for the object types that have no
 * explicit broadcast call sites (environment models, skyboxes, screen props)
 * and for shared world settings (ground, atmosphere, skybox selection,
 * spawn point).
 *
 * Instead of instrumenting ~50 mutation sites, this subscribes to the store
 * and diffs the relevant slices by reference. Local changes broadcast on the
 * world channel (env/skybox also write through to world_assets via the
 * worldBroadcast -> worldPersistence hook). Changes applied FROM the network
 * must be wrapped in withRemoteOrigin() so they never echo back out.
 *
 * Started by WorldBuilder once the world has finished loading; loading and
 * reset bulk-sets are additionally suppressed via the worldLoading flag.
 */

import { useWorldStore } from '../stores/worldStore';
import { getChannel } from './realtimeManager';
import { broadcastObjectAdd, broadcastObjectUpdate, broadcastObjectDelete } from './worldBroadcast';
import { persistObjectUpdate } from './worldPersistence';
import { applyOutsideHistory } from './worldUndo';

let remoteDepth = 0;

/** Wrap store mutations that originate from the network so they don't rebroadcast. */
export function withRemoteOrigin(fn: () => void) {
  remoteDepth++;
  // Not your undo step — patched into the history instead (services/worldUndo).
  try { applyOutsideHistory(fn); } finally { remoteDepth--; }
}

const SETTINGS_DEBOUNCE_MS = 300;

type RecordMap = Record<string, any>;

export function initWorldSettingsSync(worldId: string): () => void {
  let settingsTimer: ReturnType<typeof setTimeout> | null = null;
  let pendingSettings: RecordMap = {};

  // Sliders (atmosphere, ground) fire on every tick — coalesce into one
  // debounced settings broadcast.
  const queueSettings = (patch: RecordMap) => {
    Object.assign(pendingSettings, patch);
    if (settingsTimer) clearTimeout(settingsTimer);
    settingsTimer = setTimeout(() => {
      settingsTimer = null;
      const payload = pendingSettings;
      pendingSettings = {};
      try {
        getChannel(worldId).send({ type: 'broadcast', event: 'world:settings:updated', payload });
      } catch (_) { /* channel not ready */ }
    }, SETTINGS_DEBOUNCE_MS);
  };

  const diffRecords = (objectType: string, before: RecordMap, after: RecordMap) => {
    if (before === after) return;
    for (const id of Object.keys(after)) {
      if (!(id in before)) broadcastObjectAdd(worldId, objectType, id, after[id]);
      else if (before[id] !== after[id]) broadcastObjectUpdate(worldId, objectType, id, after[id]);
    }
    for (const id of Object.keys(before)) {
      if (!(id in after)) broadcastObjectDelete(worldId, objectType, id);
    }
  };

  const unsub = useWorldStore.subscribe((state, prevState) => {
    // Skip network-applied changes (echo), bulk load/reset, and viewers
    if (remoteDepth > 0 || state.worldLoading || !state.canBuild) return;

    diffRecords('environment', prevState.environmentModels, state.environmentModels);
    diffRecords('skybox', prevState.skyboxes, state.skyboxes);
    diffRecords('screenprop', prevState.screenProps, state.screenProps);

    if (prevState.groundSettings !== state.groundSettings) queueSettings({ ground: state.groundSettings });
    if (prevState.groundVisible !== state.groundVisible) queueSettings({ groundVisible: state.groundVisible });
    if (prevState.atmosphere !== state.atmosphere) queueSettings({ atmosphere: state.atmosphere });
    if (prevState.spawnPosition !== state.spawnPosition) queueSettings({ spawnPosition: state.spawnPosition });
    if (prevState.currentSkyboxId !== state.currentSkyboxId) {
      queueSettings({ currentSkyboxId: state.currentSkyboxId });
      // isCurrent lives in skybox row metadata — refresh both affected rows
      if (prevState.currentSkyboxId) persistObjectUpdate(worldId, 'skybox', prevState.currentSkyboxId);
      if (state.currentSkyboxId) persistObjectUpdate(worldId, 'skybox', state.currentSkyboxId);
    }
  });

  return () => {
    unsub();
    if (settingsTimer) clearTimeout(settingsTimer);
  };
}
